import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FilterDtoProps,
  FiltersDto,
} from 'components/common/ComposableFilters/FiltersDto';
import { WritableDraft } from 'immer/dist/internal';
import {
  cloneDeep,
  compact,
  forEach,
  isEmpty,
  isEqual,
  isNumber,
  orderBy,
  sortBy,
} from 'lodash';
import { RootState } from 'store';
import { segmentsApi } from './services/segmentsApi';
import type { Filters, SegmentInfo } from './services/types';

export enum MainOperatorEnum {
  And = 'and',
  Or = 'or',
}

export type SingleFilterClause = {
  key: number;
  type: 'singleFilterClause';
  property: { name: string; display: string };
  operator: { name: string; display: string };
  value: string | number | any[];
};

export type GroupedFilterClause = {
  key: number;
  type: 'groupedFilterClause';
  mainOperator: MainOperatorEnum;
  conditions: SingleFilterClause[];
};

export type FilterClauses = SingleFilterClause | GroupedFilterClause;

export interface FilterProps {
  mainOperator: MainOperatorEnum;
  conditions: FilterClauses[];
}

export interface InitialState {
  data: {
    segments: SegmentInfo[] | null;
    filters: Filters[] | null;
  };
  output: {
    filtersApiPayload: {};
  };
  internal: {
    filtersDisplayName: {
      property: Record<string, string>;
      operator: Record<string, string>;
      value: Record<string, string>;
    } | null;
    selected: {
      segmentSelectorState: {
        value: string;
        label: string;
      } | null;
    };
    applied: {
      segmentSelectorState: {
        value: string;
        label: string;
      } | null;
    };
  };
  selected: {
    segment: SegmentInfo | undefined;
    filters: FilterProps;
  };
  applied: {
    segment: SegmentInfo | undefined;
    filters: FilterProps;
  };
}

const initialState: InitialState = {
  data: {
    segments: null,
    filters: null,
  },
  output: {
    filtersApiPayload: {},
  },
  internal: {
    filtersDisplayName: localStorage.getItem(
      'segments-composableFiltersDisplayName'
    )
      ? JSON.parse(
          localStorage.getItem('segments-composableFiltersDisplayName') || ''
        )
      : null,
    selected: {
      segmentSelectorState: null,
    },

    applied: {
      segmentSelectorState: null,
    },
  },
  selected: {
    segment: undefined,
    filters: {
      mainOperator: MainOperatorEnum.And,
      conditions: [],
    },
  },
  applied: {
    segment: undefined,
    filters: {
      mainOperator: MainOperatorEnum.And,
      conditions: [],
    },
  },
};

const composableFiltersSlice = createSlice({
  initialState,
  name: 'composableFiltersState',
  reducers: {
    selectMainOperator(
      state,
      {
        payload,
      }: PayloadAction<
        | { value: MainOperatorEnum; type: 'singleFilterClause' }
        | {
            value: MainOperatorEnum;
            index: number;
            type: 'groupedFilterClause';
          }
      >
    ) {
      switch (payload.type) {
        case 'groupedFilterClause': {
          const condition = state.selected.filters.conditions[payload.index];

          if (condition.type === 'groupedFilterClause') {
            condition.mainOperator = payload.value;
          }

          break;
        }
        case 'singleFilterClause': {
          state.selected.filters.mainOperator = payload.value;
        }
      }

      composableFiltersSlice.caseReducers.matchFiltersToSegment(state);
    },
    addFilterClause(
      state,
      {
        payload,
      }: PayloadAction<{
        clause?: SingleFilterClause | GroupedFilterClause;
        type: 'singleFilterClause' | 'groupedFilterClause';
        parentIndex?: number;
      }>
    ) {
      switch (payload.type) {
        case 'singleFilterClause': {
          if (payload.clause) {
            state.selected.filters.conditions.push(payload.clause);
          } else {
            const emptyFilterClause: SingleFilterClause = {
              property: { name: '', display: '' },
              operator: { name: '', display: '' },
              value: '',
              key: Math.random(),
              type: 'singleFilterClause',
            };

            state.selected.filters.conditions.push(emptyFilterClause);
          }
          break;
        }
        case 'groupedFilterClause': {
          if (payload.clause) {
            state.selected.filters.conditions.push(payload.clause);
          } else {
            if (isNumber(payload.parentIndex)) {
              const parent =
                state.selected.filters.conditions[payload.parentIndex];

              if (parent.type === 'groupedFilterClause') {
                const emptyFilterClause: SingleFilterClause = {
                  property: { name: '', display: '' },
                  operator: { name: '', display: '' },
                  value: '',
                  key: Math.random(),
                  type: 'singleFilterClause',
                };

                parent.conditions.push(emptyFilterClause);
              }
            } else {
              const groupedClause: GroupedFilterClause = {
                type: 'groupedFilterClause',
                key: Math.random(),
                mainOperator: MainOperatorEnum.And,
                conditions: [
                  {
                    type: 'singleFilterClause',
                    property: { name: '', display: '' },
                    operator: { name: '', display: '' },
                    value: '',
                    key: Math.random(),
                  },
                ],
              };

              state.selected.filters.conditions.push(groupedClause);
            }
          }
        }
      }

      composableFiltersSlice.caseReducers.matchFiltersToSegment(state);
    },
    applySelected(state) {
      validateSelectedFilters(state);

      if (state.selected.filters.conditions.length > 0) {
        state.applied = {
          ...state.selected,
        };

        state.internal.applied = { ...state.internal.selected };

        state.output.filtersApiPayload = new FiltersDto(
          state.applied.filters
        ).generateBodyPayload();
      } else {
        state.applied = { ...initialState.applied };
        state.internal.applied = { ...initialState.internal.applied };
        composableFiltersSlice.caseReducers.clearOutput(state);
      }
    },
    updateFilterClause(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            data: Partial<SingleFilterClause>;
            type: 'singleFilterClause';
            index: number;
          }
        | {
            data: Partial<SingleFilterClause>;
            type: 'groupedFilterClause';
            index: number;
            parentIndex: number;
          }
      >
    ) {
      switch (payload.type) {
        case 'singleFilterClause': {
          const selectedFilterClause = state.selected.filters.conditions[
            payload.index
          ] as WritableDraft<SingleFilterClause>;

          forEach(payload.data, (value, key) => {
            // @ts-ignore
            selectedFilterClause[key] = value;

            const hasValue = Array.isArray(selectedFilterClause['value'])
              ? Boolean(selectedFilterClause.value.length)
              : isNumber(selectedFilterClause.value)
              ? true
              : Boolean(selectedFilterClause.value);

            if ((key === 'operator' && hasValue) || key === 'value') {
              composableFiltersSlice.caseReducers.matchFiltersToSegment(state);
            }
          });

          break;
        }
        case 'groupedFilterClause': {
          const parent = state.selected.filters.conditions[payload.parentIndex];

          if (
            parent.type === 'groupedFilterClause' &&
            parent.conditions.length > 0
          ) {
            for (let key in payload.data) {
              const children = parent.conditions[payload.index];
              //@ts-ignore
              children[key] = payload.data[key];

              if (
                (key === 'operator' && !!!isEmpty(children.value)) ||
                key === 'value'
              ) {
                composableFiltersSlice.caseReducers.matchFiltersToSegment(
                  state
                );
              }
            }
          }

          break;
        }
      }
    },
    removeFilterClause(
      state,
      {
        payload,
      }: PayloadAction<
        | {
            index: number;
            type: 'singleFilterClause';
          }
        | {
            index: number;
            type: 'groupedFilterClause';
            parentIndex: number;
          }
      >
    ) {
      switch (payload.type) {
        case 'singleFilterClause': {
          state.selected.filters.conditions.splice(payload.index, 1);

          break;
        }

        case 'groupedFilterClause': {
          const parent = state.selected.filters.conditions[payload.parentIndex];

          if (parent.type === 'groupedFilterClause') {
            parent.conditions.splice(payload.index, 1);
          }

          break;
        }
      }
      composableFiltersSlice.caseReducers.matchFiltersToSegment(state);
    },

    cancelApplying(state) {
      state.selected = { ...state.applied };
      state.internal.selected = { ...state.internal.applied };
    },
    clearSelected(state) {
      state.selected = { ...initialState.selected };
      state.internal.selected.segmentSelectorState = null;
    },
    clearApplied(state) {
      state.selected = { ...initialState.selected };
      state.applied = { ...initialState.applied };

      state.internal.selected.segmentSelectorState = null;
      state.internal.applied.segmentSelectorState = null;
      composableFiltersSlice.caseReducers.clearOutput(state);
    },
    clearOutput(state) {
      state.output.filtersApiPayload = {};
    },
    // Segment methods
    selectSegment(
      state,
      { payload }: PayloadAction<{ segmentName: string; directApply?: boolean }>
    ) {
      const selectedSegment = state.data.segments?.find(
        (segment) =>
          segment.segment_name.toLowerCase() ===
          payload.segmentName.toLowerCase()
      );

      if (selectedSegment) {
        const selectorState = {
          value: selectedSegment.segment_name,
          label: selectedSegment.segment_name,
        };

        const filters = matchSegmentToFilters({
          segment: selectedSegment,
          state,
        });

        if (payload.directApply) {
          state.selected.segment = selectedSegment;
          state.applied.segment = selectedSegment;

          state.selected.filters = filters;
          state.applied.filters = filters;

          state.internal.selected.segmentSelectorState = selectorState;
          state.internal.applied.segmentSelectorState = selectorState;
          state.output.filtersApiPayload = new FiltersDto(
            state.applied.filters
          ).generateBodyPayload();
        } else {
          state.selected.segment = selectedSegment;

          state.selected.filters = filters;

          state.internal.selected.segmentSelectorState = selectorState;
        }
      }
    },
    matchFiltersToSegment(state) {
      const filterJson = new FiltersDto(
        state.selected.filters
      ).generateBodyPayload();

      const matchedSegment = state.data.segments?.find((filter) => {
        return isEqual(
          handleSortFilters(filterJson),
          handleSortFilters(filter.json_filter as FilterDtoProps)
        );
      });

      if (matchedSegment) {
        state.selected.segment = matchedSegment;
        state.internal.selected.segmentSelectorState = {
          value: matchedSegment.segment_name,
          label: matchedSegment.segment_name,
        };
      } else {
        state.internal.selected.segmentSelectorState = null;
        state.selected.segment = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        segmentsApi.endpoints.getCustomerSegments.matchFulfilled,
        (state, { payload }) => {
          state.data.segments = payload;

          if (state.selected.segment) {
            const updatedSegment = state.data.segments.find(
              (item) => item.id === state.selected.segment?.id
            );

            state.selected.segment = updatedSegment;
            state.applied.segment = updatedSegment;

            state.internal.selected.segmentSelectorState = updatedSegment
              ? {
                  value: updatedSegment.segment_name,
                  label: updatedSegment.segment_name,
                }
              : null;
          }

          if (state.selected.filters.conditions.length) {
            composableFiltersSlice.caseReducers.matchFiltersToSegment(state);
            composableFiltersSlice.caseReducers.applySelected(state);
          }
        }
      )
      .addMatcher(
        segmentsApi.endpoints.getFilters.matchFulfilled,
        (state, { payload }) => {
          state.data.filters = sortBy(payload, ['name']);
          // console.time('generating');
          state.internal.filtersDisplayName = generateFiltersDisplayName({
            payload,
          });
          // console.timeEnd('generating');
        }
      );
  },
});

export default composableFiltersSlice.reducer;

export const {
  addFilterClause,
  applySelected,
  cancelApplying,
  clearApplied,
  clearOutput,
  clearSelected,
  removeFilterClause,
  selectMainOperator,
  selectSegment,
  updateFilterClause,
} = composableFiltersSlice.actions;

export const composableFiltersState = (state: RootState) =>
  state.composableFiltersState;

interface GenerateFiltersDisplayName {
  payload: Filters[];
}

function generateFiltersDisplayName({ payload }: GenerateFiltersDisplayName) {
  const property: Record<string, string> = {};
  const operator: Record<string, string> = {};
  const value: Record<string, string> = {};

  payload.forEach((filter) => {
    property[filter.name] = filter.display;

    filter.operators.forEach((operatorItem) => {
      operator[filter.name + '_' + operatorItem.name] = operatorItem.display;
    });

    filter.values?.forEach((v) => {
      value[v.name] = v.display;
    });
  });

  try {
    localStorage.setItem(
      'segments-composableFiltersDisplayName',
      JSON.stringify({
        property,
        operator,
        value,
      })
    );
  } catch (error) {
    console.error(error);
  }

  return {
    property,
    operator,
    value,
  };
}

function validateSelectedFilters(state: WritableDraft<InitialState>) {
  state.selected.filters.conditions = compact(
    state.selected.filters.conditions.filter((clause) => {
      switch (clause.type) {
        case 'groupedFilterClause': {
          return clause.conditions?.filter((nestedClause) => {
            const isOperatorWithoutValue =
              nestedClause.operator?.name &&
              ['empty', 'not_empty'].includes(nestedClause.operator?.name);

            if (isOperatorWithoutValue) {
              return nestedClause.operator?.name && nestedClause.property?.name;
            } else {
              return (
                (Array.isArray(nestedClause.value)
                  ? nestedClause.value.length
                  : isNumber(nestedClause.value)
                  ? String(nestedClause.value)
                  : nestedClause.value) &&
                nestedClause.operator?.name &&
                nestedClause.property?.name
              );
            }
          });
        }

        case 'singleFilterClause': {
          const isOperatorWithoutValue =
            clause.operator?.name &&
            ['empty', 'not_empty'].includes(clause.operator?.name);

          if (isOperatorWithoutValue) {
            return clause.operator?.name && clause.property?.name;
          } else {
            return (
              (Array.isArray(clause.value)
                ? clause.value.length
                : isNumber(clause.value)
                ? String(clause.value)
                : clause.value) &&
              clause.operator?.name &&
              clause.property?.name
            );
          }
        }
        default: {
          return null;
        }
      }
    })
  );
}

function handleSortFilters(jsonFilter: FilterDtoProps) {
  if (isEmpty(jsonFilter)) return;

  const out = cloneDeep(jsonFilter);

  out.filter.filters = orderBy(
    jsonFilter.filter.filters,
    (item) => item.name,
    'asc'
  );

  return out;
}

interface MatchSegmentToFilters {
  segment: SegmentInfo;
  state: WritableDraft<InitialState>;
}

function matchSegmentToFilters({
  segment,
  state,
}: MatchSegmentToFilters): FilterProps {
  const filtersArray = segment.json_filter?.filter?.filters;

  const conditions: FilterClauses[] = [];

  filtersArray?.forEach((item) => {
    // If there is filter_operator, it means it is a Nested/Grouped filter
    if ('filter_operator' in item) {
      const filterClause: GroupedFilterClause = {
        key: Math.random(),
        type: 'groupedFilterClause',
        mainOperator: item.filter_operator as MainOperatorEnum,
        conditions: [] as any,
      };
      if (item.filters?.length) {
        item.filters.forEach((nestedItem: any) => {
          const nestedClause: SingleFilterClause = {
            property: {
              name: nestedItem.name,
              display:
                state.internal.filtersDisplayName?.property[nestedItem.name] ||
                nestedItem.name,
            },
            operator: {
              name: nestedItem.operator,
              display:
                state.internal.filtersDisplayName?.operator[
                  nestedItem.name + '_' + nestedItem.operator
                ] || nestedItem.operator,
            },
            value: Array.isArray(nestedItem.value)
              ? nestedItem.value.map((v: any) => ({
                  value: v,
                  label: state.internal.filtersDisplayName?.value[v],
                }))
              : nestedItem.value,
            key: Math.random(),
            type: 'singleFilterClause',
          };

          filterClause.conditions.push(nestedClause);
        });
        conditions.push(filterClause);
      }

      return;
    } else {
      if (item.name && item.operator) {
        const filterClause: SingleFilterClause = {
          property: {
            name: item.name,
            display:
              state.internal.filtersDisplayName?.property[item.name] ||
              item.name,
          },
          operator: {
            name: item.operator,
            display:
              state.internal.filtersDisplayName?.operator[
                item.name + '_' + item.operator
              ] || item.operator,
          },
          value: Array.isArray(item.value)
            ? item.value.map((v: any) => ({
                value: v,
                label: state.internal.filtersDisplayName?.value[v],
              }))
            : item.value,
          key: Math.random(),
          type: 'singleFilterClause',
        };
        conditions.push(filterClause);
      }
    }
  });

  return {
    mainOperator: segment.json_filter?.filter
      ?.filter_operator as MainOperatorEnum,
    conditions,
  };
}
